// lib/koi-pond-v2/shaders/koiPattern.ts
//
// Procedural koi pattern fragment shader. Branches on uColorwayId so a
// single program serves all five colorways:
//   0 = kohaku  (cream + vermillion patches + optional sumi)
//   1 = ogon    (metallic gold, anisotropic streak, spine highlight)
//   2 = karasu  (deep black + broken-segment gold dorsal stripe)
//   3 = sanke   (cream + vermillion patches + sumi accents)
//   4 = tancho  (cream + single red disc near head)
//
// uSeed (float in [0, 1)) seeds per-fish randomization so multiple koi
// of the same colorway don't render identical patches.
//
// Tasks 3-7 add one colorway each; the file is the visible diff at each
// step. Phase 1 panels render this shader on a flat plane (no koi
// geometry yet) so the marking design can be approved at thumbnail size
// before the geometry lands in Task 13.

import { ShaderMaterial } from 'three';
import { palette, paletteVec3 } from '../palette';

export const KOI_PATTERN_VERT = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// Full fragment shader with all five colorway branches stubbed.
// Each task (3-7) replaces ONE branch's body with the real implementation.
// Task 3 fills in the `if (uColorwayId == 0)` (kohaku) branch.
export const KOI_PATTERN_FRAG = `
  precision highp float;
  varying vec2 vUv;
  uniform float uSeed;
  uniform int uColorwayId;

  // Hash + simplex helpers
  float hash(vec2 p) {
    p = fract(p * vec2(123.34, 456.21));
    p += dot(p, p + 45.32);
    return fract(p.x * p.y);
  }
  vec2 hash2(vec2 p, float seed) {
    return vec2(hash(p + seed), hash(p + seed + 17.0));
  }
  float patchSdf(vec2 uv, vec2 center, vec2 radii) {
    vec2 d = (uv - center) / radii;
    return 1.0 - smoothstep(0.7, 1.0, length(d));
  }
  float valueNoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    float a = hash(i);
    float b = hash(i + vec2(1.0, 0.0));
    float c = hash(i + vec2(0.0, 1.0));
    float d = hash(i + vec2(1.0, 1.0));
    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }

  void main() {
    vec2 uv = vUv;
    vec3 base = vec3(1.0, 0.0, 1.0); // Magenta = "branch not implemented yet"

    if (uColorwayId == 0) {
      // Kohaku — cream body + 3-5 vermillion patches + 1-2 sumi accents
      base = ${paletteVec3('kohakuWhite')};
      for (int i = 0; i < 5; i++) {
        vec2 cseed = hash2(vec2(float(i) * 7.13, 0.5), uSeed);
        vec2 center = vec2(0.18 + cseed.x * 0.6, 0.18 + cseed.y * 0.25);
        vec2 radii = vec2(0.08 + cseed.x * 0.04, 0.12 + cseed.y * 0.05);
        float t = patchSdf(uv, center, radii);
        float edgeN = valueNoise((uv - center) * 30.0) * 0.15;
        t *= 1.0 + edgeN;
        base = mix(base, ${paletteVec3('kohakuRed')}, clamp(t, 0.0, 1.0));
      }
      for (int i = 0; i < 2; i++) {
        vec2 cseed = hash2(vec2(float(i) * 13.0, 0.8), uSeed);
        vec2 center = vec2(0.3 + cseed.x * 0.4, 0.12 + cseed.y * 0.16);
        float t = patchSdf(uv, center, vec2(0.025, 0.05));
        base = mix(base, ${paletteVec3('sankeBlack')}, clamp(t, 0.0, 1.0));
      }
    } else if (uColorwayId == 1) {
      // Ogon — uniform metallic gold with subtle dorsal spine highlight
      // and a horizontal anisotropic streak. No SDF patches — the whole
      // body is the marking. Brief acceptance: "uniformly metallic gold
      // with subtle anisotropic streak."
      base = ${paletteVec3('ogonGold')};
      // Dorsal spine brightening (V near 0.15 = top of back at thumbnail UV)
      float spine = exp(-pow((uv.y - 0.15) * 8.0, 2.0)) * 0.15;
      base += vec3(spine);
      // Anisotropic horizontal streak at body mid-line
      float streak = exp(-pow((uv.y - 0.4) * 12.0, 2.0)) * 0.05;
      base += vec3(streak, streak * 0.85, streak * 0.6);
    } else if (uColorwayId == 2) {
      // Karasu — deep black base + broken-segment gold dorsal stripe +
      // 1 lateral gold patch. Brief acceptance: "deep near-black with a
      // thin gold dorsal stripe." The dorsal segments multiply ogonGold
      // by 0.7 and the lateral by 0.6 so they read as muted dim gold
      // against the karasu base, not bright like ogon's body.
      base = ${paletteVec3('karasuBlack')};
      // 5 dorsal gold segments at V near 0.06 (top of UV = dorsal in
      // koi UV convention). Tiny 0.04 x 0.04 patches give the
      // "broken-segment thin stripe" silhouette.
      for (int i = 0; i < 5; i++) {
        vec2 cseed = hash2(vec2(float(i) * 11.0, 0.1), uSeed);
        vec2 center = vec2(0.18 + float(i) * 0.13 + cseed.x * 0.03, 0.06 + cseed.y * 0.05);
        float t = patchSdf(uv, center, vec2(0.04, 0.04));
        base = mix(base, ${paletteVec3('ogonGold')} * 0.7, clamp(t, 0.0, 1.0));
      }
      // 1 lateral gold patch mid-body (V ~0.35, larger 0.06 x 0.10
      // radii so it reads as a distinct flank marking, not another
      // dorsal segment).
      vec2 lateralC = hash2(vec2(99.0, 99.0), uSeed) * 0.1 + vec2(0.45, 0.35);
      float lt = patchSdf(uv, lateralC, vec2(0.06, 0.1));
      base = mix(base, ${paletteVec3('ogonGold')} * 0.6, clamp(lt, 0.0, 1.0));
    } else if (uColorwayId == 3) {
      // Sanke — cream + 3 vermillion patches + 3 sumi accents positioned
      // in the gaps BETWEEN vermillion. Vermillion centers at x ≈ 0.20,
      // 0.47, 0.74. Sumi centers at x ≈ 0.34, 0.61, 0.88 — landing in
      // the two inter-patch gaps plus a tail-end position. The third
      // sumi position was originally 0.74 in the plan, which collided
      // exactly with the third vermillion; shifted to 0.88 so the user's
      // pixel-readback verification can distinguish all six patch
      // regions cleanly. Brief acceptance: "cream base with both
      // vermillion and black patches."
      base = ${paletteVec3('kohakuWhite')};
      for (int i = 0; i < 3; i++) {
        vec2 cseed = hash2(vec2(float(i) * 9.0, 0.5), uSeed);
        vec2 center = vec2(0.2 + float(i) * 0.27 + cseed.x * 0.04, 0.18 + cseed.y * 0.25);
        vec2 radii = vec2(0.07 + cseed.x * 0.04, 0.13 + cseed.y * 0.06);
        float t = patchSdf(uv, center, radii);
        base = mix(base, ${paletteVec3('kohakuRed')}, clamp(t, 0.0, 1.0));
      }
      for (int i = 0; i < 3; i++) {
        vec2 cseed = hash2(vec2(float(i) * 19.0, 0.8), uSeed);
        vec2 center = vec2(0.34 + float(i) * 0.27 + cseed.x * 0.04, 0.1 + cseed.y * 0.18);
        float t = patchSdf(uv, center, vec2(0.025, 0.05));
        base = mix(base, ${paletteVec3('sankeBlack')}, clamp(t, 0.0, 1.0));
      }
    } else if (uColorwayId == 4) {
      // Tancho — pure cream-white + a single red disc near the head.
      // The classic Tancho mark, highly recognizable silhouette. Brief
      // acceptance: "exactly one red disc near the head." Disc at UV
      // (0.15, 0.18) — head end of the body (U=0 is head end in the
      // koi UV convention). Slightly elongated radii (0.045 x 0.10) so
      // the disc reads as oval rather than perfectly circular — matches
      // typical tancho head-marking shape on a real koi.
      base = ${paletteVec3('kohakuWhite')};
      vec2 center = vec2(0.15, 0.18);
      vec2 radii = vec2(0.045, 0.10);
      float t = patchSdf(uv, center, radii);
      base = mix(base, ${paletteVec3('tanchoRed')}, clamp(t, 0.0, 1.0));
    }

    // Subtle simplex grain across all colorways
    float g = valueNoise(uv * 80.0) * 2.0 - 1.0;
    base += vec3(g * 0.025);

    gl_FragColor = vec4(base, 1.0);
  }
`;

export type Colorway = 'kohaku' | 'ogon' | 'karasu' | 'sanke' | 'tancho';
const COLORWAY_INDEX: Record<Colorway, number> = {
  kohaku: 0,
  ogon: 1,
  karasu: 2,
  sanke: 3,
  tancho: 4,
};

export function makeKoiPatternMaterial(colorway: Colorway, seed = 0.42): ShaderMaterial {
  return new ShaderMaterial({
    vertexShader: KOI_PATTERN_VERT,
    fragmentShader: KOI_PATTERN_FRAG,
    uniforms: {
      uSeed: { value: seed },
      uColorwayId: { value: COLORWAY_INDEX[colorway] },
    },
  });
}

// Palette is re-exported so the constants are emitted in the bundle next
// to the shader — keeps the visual contract co-located.
export { palette };
